import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Sparkles, Loader2, CheckCircle2, AlertTriangle, RotateCcw, Trash2, RefreshCw, FileCheck2 } from "lucide-react";
import api from "@/lib/api";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

const CONF_COLOR = (c) => c >= 85 ? "bg-emerald-100 text-emerald-700"
  : c >= 60 ? "bg-amber-100 text-amber-700"
  : "bg-red-100 text-red-700";

export default function ReconcilePanel({ projects = [] }) {
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [rows, setRows] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);

  // Build a lookup for line dropdown options
  const linesByProject = (() => {
    const m = {};
    for (const p of projects) {
      m[p.id] = { name: p.name, status: p.status, lines: p.billing_lines || [] };
    }
    return m;
  })();

  const loadBatches = useCallback(async () => {
    setLoadingBatches(true);
    try { setBatches((await api.get("/collections/reconcile/batches")).data); }
    catch { toast.error("Failed to load reconcile history"); }
    setLoadingBatches(false);
  }, []);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  const loadBatch = useCallback(async (id) => {
    try {
      const r = await api.get(`/collections/reconcile/batches/${id}`);
      setSelectedBatch(r.data.batch);
      setRows(r.data.rows);
    } catch { toast.error("Failed to load batch"); }
  }, []);

  const upload = async () => {
    if (!file) { toast.error("Choose a CSV or XLSX file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/collections/reconcile/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Parsed ${r.data.summary.row_count} credit rows · ${r.data.summary.matched} auto-matched`);
      setFile(null);
      await loadBatches();
      await loadBatch(r.data.batch_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const updateRow = async (rowId, patch) => {
    if (!selectedBatch) return;
    // Optimistic update
    setRows(rs => rs.map(r => r.id === rowId ? { ...r, ...patch } : r));
    try { await api.put(`/collections/reconcile/batches/${selectedBatch.id}/rows/${rowId}`, patch); }
    catch (e) { toast.error("Update failed"); loadBatch(selectedBatch.id); }
  };

  const bulkApprove = (minConf) => {
    const toUpdate = rows.filter(r => r.status === "review" && (r.ai_confidence || 0) >= minConf && r.ai_billing_line_id);
    if (toUpdate.length === 0) { toast.info(`No review rows with confidence ≥${minConf}%`); return; }
    Promise.all(toUpdate.map(r => updateRow(r.id, { status: "matched", final_project_id: r.ai_project_id, final_billing_line_id: r.ai_billing_line_id }))).then(() => {
      toast.success(`Approved ${toUpdate.length} row${toUpdate.length !== 1 ? "s" : ""}`);
    });
  };

  const commit = async () => {
    if (!selectedBatch) return;
    if (!window.confirm("Create receipts for all matched rows? This action can be undone via 'Undo Batch'.")) return;
    setCommitting(true);
    try {
      const r = await api.post(`/collections/reconcile/batches/${selectedBatch.id}/commit`, {});
      toast.success(`Created ${r.data.created} receipt${r.data.created !== 1 ? "s" : ""}`);
      await loadBatches();
      await loadBatch(selectedBatch.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Commit failed"); }
    setCommitting(false);
  };

  const undo = async () => {
    if (!selectedBatch) return;
    if (!window.confirm(`Undo this batch? All ${selectedBatch.committed_count} receipts created by it will be permanently deleted.`)) return;
    try {
      const r = await api.post(`/collections/reconcile/batches/${selectedBatch.id}/undo`, {});
      toast.success(`Removed ${r.data.removed_receipts} receipts`);
      await loadBatches();
      await loadBatch(selectedBatch.id);
    } catch (e) { toast.error("Undo failed"); }
  };

  const deleteBatch = async () => {
    if (!selectedBatch) return;
    if (!window.confirm("Delete this uncommitted batch? Staged rows will be discarded.")) return;
    try {
      await api.delete(`/collections/reconcile/batches/${selectedBatch.id}`);
      setSelectedBatch(null);
      setRows([]);
      await loadBatches();
      toast.success("Batch deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const matchedCount = rows.filter(r => r.status === "matched").length;
  const reviewCount = rows.filter(r => r.status === "review").length;
  const committedCount = rows.filter(r => r.status === "committed").length;
  const skippedCount = rows.filter(r => r.status === "skipped").length;
  const dupCount = rows.filter(r => r.status === "duplicate").length;
  const totalMatchedAmt = rows.filter(r => r.status === "matched").reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div className="space-y-4" data-testid="reconcile-panel">
      {/* Upload */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-violet-600" /> AI Bank Reconciliation
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5">Upload a bank statement (CSV / XLSX). Claude Haiku 4.5 will match each credit against your open billing lines. Confidence ≥60% is auto-selected — review, then commit.</p>
            </div>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <Label className="text-xs">Bank statement (.csv or .xlsx)</Label>
              <Input type="file" accept=".csv,.xlsx,.xls" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="reconcile-file-input" />
            </div>
            <Button onClick={upload} disabled={uploading || !file} className="bg-violet-600 hover:bg-violet-700 gap-1.5" data-testid="reconcile-upload-btn">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? "Analysing…" : "Upload & Match"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Batch history */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <h4 className="text-sm font-semibold text-slate-800">Recent Reconciliation Batches</h4>
            <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={loadBatches}><RefreshCw className="w-3 h-3" /> Refresh</Button>
          </div>
          {loadingBatches ? <div className="p-6 text-center"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div> :
            batches.length === 0 ? <p className="p-8 text-center text-xs text-slate-400 italic">No batches yet. Upload a bank statement to get started.</p> :
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full text-xs" data-testid="reconcile-batches-table">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2.5 font-semibold text-slate-600">Uploaded</th>
                      <th className="text-left p-2.5 font-semibold text-slate-600">File</th>
                      <th className="text-right p-2.5 font-semibold text-slate-600">Rows</th>
                      <th className="text-right p-2.5 font-semibold text-slate-600">Matched</th>
                      <th className="text-right p-2.5 font-semibold text-slate-600">Review</th>
                      <th className="text-right p-2.5 font-semibold text-slate-600">Duplicates</th>
                      <th className="text-right p-2.5 font-semibold text-slate-600">Committed</th>
                      <th className="text-left p-2.5 font-semibold text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map(b => (
                      <tr key={b.id} onClick={() => loadBatch(b.id)}
                          className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${selectedBatch?.id === b.id ? "bg-blue-50" : ""}`}
                          data-testid={`reconcile-batch-${b.id}`}>
                        <td className="p-2.5 whitespace-nowrap">{fmtDate(b.uploaded_at)}</td>
                        <td className="p-2.5 font-medium text-slate-800 max-w-[220px] truncate">{b.filename}</td>
                        <td className="p-2.5 text-right">{b.row_count}</td>
                        <td className="p-2.5 text-right text-emerald-600 font-semibold">{b.matched_count}</td>
                        <td className="p-2.5 text-right text-amber-600 font-semibold">{b.review_count}</td>
                        <td className="p-2.5 text-right text-slate-500">{b.duplicate_count}</td>
                        <td className="p-2.5 text-right text-[#0066CC] font-semibold">{b.committed_count}</td>
                        <td className="p-2.5">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${b.status === "committed" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                            {b.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          }
        </CardContent>
      </Card>

      {/* Batch detail — rows */}
      {selectedBatch && (
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center justify-between flex-wrap gap-2 p-4 border-b border-slate-100">
              <div>
                <h4 className="text-sm font-semibold text-slate-800">
                  Batch — {selectedBatch.filename} <span className="text-[11px] text-slate-500 ml-2">{fmtDate(selectedBatch.uploaded_at)}</span>
                </h4>
                <div className="flex items-center gap-3 mt-1 text-[11px]">
                  <span>Matched <strong className="text-emerald-600">{matchedCount}</strong> · ₹{fmt(totalMatchedAmt)}</span>
                  <span>Review <strong className="text-amber-600">{reviewCount}</strong></span>
                  {dupCount > 0 && <span>Duplicates <strong className="text-slate-500">{dupCount}</strong></span>}
                  {skippedCount > 0 && <span>Skipped <strong className="text-slate-500">{skippedCount}</strong></span>}
                  {committedCount > 0 && <span>Committed <strong className="text-[#0066CC]">{committedCount}</strong></span>}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => bulkApprove(85)} data-testid="reconcile-approve-85">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Approve ≥85%
                </Button>
                <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => bulkApprove(60)} data-testid="reconcile-approve-60">
                  <CheckCircle2 className="w-3.5 h-3.5 text-amber-600" /> Approve ≥60%
                </Button>
                {selectedBatch.status !== "committed" ? (
                  <>
                    <Button size="sm" onClick={commit} disabled={committing || matchedCount === 0} className="bg-[#0066CC] gap-1.5" data-testid="reconcile-commit-btn">
                      {committing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileCheck2 className="w-3.5 h-3.5" />}
                      Commit {matchedCount} receipt{matchedCount !== 1 ? "s" : ""}
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600 gap-1 text-xs" onClick={deleteBatch}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </>
                ) : (
                  <Button size="sm" variant="outline" className="gap-1 text-xs text-red-600" onClick={undo} data-testid="reconcile-undo-btn">
                    <RotateCcw className="w-3.5 h-3.5" /> Undo Batch
                  </Button>
                )}
              </div>
            </div>

            <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
              <table className="w-full text-xs" data-testid="reconcile-rows-table">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="text-left p-2.5 font-semibold text-slate-600 whitespace-nowrap">Date</th>
                    <th className="text-left p-2.5 font-semibold text-slate-600">Narration</th>
                    <th className="text-right p-2.5 font-semibold text-slate-600">Amount</th>
                    <th className="text-left p-2.5 font-semibold text-slate-600">AI Match</th>
                    <th className="text-center p-2.5 font-semibold text-slate-600">Confidence</th>
                    <th className="text-left p-2.5 font-semibold text-slate-600">Project</th>
                    <th className="text-left p-2.5 font-semibold text-slate-600">Billing Line</th>
                    <th className="text-center p-2.5 font-semibold text-slate-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const readonly = r.status === "committed" || r.status === "duplicate";
                    const proj = r.final_project_id ? linesByProject[r.final_project_id] : null;
                    return (
                      <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="p-2.5 whitespace-nowrap text-slate-600">{r.date}</td>
                        <td className="p-2.5 text-slate-700 max-w-[280px] truncate" title={r.narration}>{r.narration}</td>
                        <td className="p-2.5 text-right font-mono tabular-nums text-emerald-700 font-semibold">₹{fmt(r.amount)}</td>
                        <td className="p-2.5 text-slate-500 max-w-[250px] text-[10px]" title={r.ai_reasoning}>{r.ai_reasoning?.slice(0, 90)}</td>
                        <td className="p-2.5 text-center">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${CONF_COLOR(r.ai_confidence)}`}>{r.ai_confidence}%</span>
                        </td>
                        <td className="p-2.5">
                          <select
                            value={r.final_project_id || ""}
                            disabled={readonly}
                            onChange={e => updateRow(r.id, { final_project_id: e.target.value, final_billing_line_id: "" })}
                            className="h-8 w-full text-xs rounded border border-slate-200 bg-white px-1.5 disabled:bg-slate-50"
                          >
                            <option value="">— none —</option>
                            {projects.filter(p => p.status === "active").map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </td>
                        <td className="p-2.5">
                          <select
                            value={r.final_billing_line_id || ""}
                            disabled={readonly || !r.final_project_id}
                            onChange={e => updateRow(r.id, { final_billing_line_id: e.target.value })}
                            className="h-8 w-full text-xs rounded border border-slate-200 bg-white px-1.5 disabled:bg-slate-50"
                          >
                            <option value="">— none —</option>
                            {(proj?.lines || []).map(l => <option key={l.id} value={l.id}>{l.type} · {l.frequency?.replace("_", " ")} · ₹{fmt(l.amount)}</option>)}
                          </select>
                        </td>
                        <td className="p-2.5 text-center">
                          {r.status === "committed" ? <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">committed</span> :
                            r.status === "duplicate" ? <span className="text-[10px] font-semibold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">duplicate</span> :
                              r.status === "matched" ? (
                                <Button size="sm" variant="ghost" className="h-6 text-[10px] text-slate-500" onClick={() => updateRow(r.id, { status: "skipped" })} data-testid={`reconcile-skip-${r.id}`}>Skip</Button>
                              ) : r.status === "skipped" ? (
                                <Button size="sm" variant="ghost" className="h-6 text-[10px] text-emerald-600" onClick={() => updateRow(r.id, { status: r.final_billing_line_id ? "matched" : "review" })}>Include</Button>
                              ) : (
                                <Button size="sm" variant="ghost" className="h-6 text-[10px] text-emerald-600 disabled:opacity-40" disabled={!r.final_billing_line_id} onClick={() => updateRow(r.id, { status: "matched" })} data-testid={`reconcile-approve-${r.id}`}>
                                  <CheckCircle2 className="w-3 h-3 mr-0.5" /> Approve
                                </Button>
                              )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
